import { useState, useCallback } from 'react';
import type { AxiosResponse } from 'axios'; // Import AxiosResponse
import { searchByQuery, getGroupTTPs, getGroupSoftware, getGroupYaraRules } from '@/lib/api/search';
import { api } from '@/lib/api/config';
import { searchMalware } from '@/lib/api/malware';
import type { MitreGroup } from '@/lib/types/api';
import type { SearchResult } from '@/lib/types/search';
import type { SandboxAnalysis } from '@/lib/types/sandbox';
import type { MalwareIntelligence, MalwareResponse } from '@/lib/types/malware'; // Import MalwareResponse
import { useAuth } from '@/lib/store';

interface SearchState {
  results: SearchResult[];
  sandboxResults: SandboxAnalysis[];
  malwareResults: MalwareIntelligence[];
  isLoading: boolean;
  error: string | null;
}

export function useSearch() {
  const [state, setState] = useState<SearchState>({
    results: [],
    sandboxResults: [],
    malwareResults: [],
    isLoading: false,
    error: null,
  });
  
  const logout = useAuth((state) => state.logout);

  const fetchGroupData = async (group: MitreGroup, index: number) => {
    try {
      setState((prev) => ({
        ...prev,
        results: prev.results.map((r, i) => 
          i === index ? { ...r, isLoading: true } : r
        ),
      }));

      // Fetch TTPs, software, and Yara rules in parallel
      const [ttps, software, yaraRules] = await Promise.all([
        getGroupTTPs(group.group_id),
        getGroupSoftware(group.group_id),
        getGroupYaraRules(group.group_id),
      ]);

      console.log(`Fetched data for group ${group.group_id}:`, {
        ttps: ttps.length,
        software: software.length,
        yaraRules: yaraRules.length
      });

      setState((prev) => ({
        ...prev,
        results: prev.results.map((r, i) => 
          i === index ? {
            ...r,
            ttps,
            software,
            yaraRules,
            isLoading: false,
            enrichedData: {  
              name: group.common_name,
              mitreId: group.group_id,
              mitreUrl: group.link,
              country: group.coutry, // Use correct backend field
              firstseen: group.year,   // Use correct backend field
              summary: '',
              targets: [],
              aliases: [],
              incidents: [],
              tactics: [],
              enrichment: [],
            },
          } : r
        ),
      }));
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'Authentication failed') {
        logout();
        return;
      }
      console.error(`Error enriching data for group ${group.group_id}:`, error);
      setState(prev => ({
        ...prev,
        results: prev.results.map((r, i) => 
          i === index ? { ...r, isLoading: false } : r
        ),
      }));
    }
  };

  const search = useCallback(async (query: string) => {
    if (!query.trim()) return;
    
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    const searchPromises = [
      searchByQuery(query),
      api.get<SandboxAnalysis[]>(`/api/sandbox/hybrid/hash/${encodeURIComponent(query)}`).catch(() => ({ data: [] })),
      searchMalware(query).catch(() => ({ query_status: 'error', data: [] }))
    ];

    try {
      // Explicitly type the results from Promise.all
      const [
        groupResults,
        sandboxResponse,
        malwareResponse
      ] = await Promise.all(searchPromises) as [
        MitreGroup[],
        AxiosResponse<SandboxAnalysis[]> | { data: [] },
        MalwareResponse | { query_status: 'error', data: [] } // Use imported MalwareResponse
      ];

      // Type guard for malware response
      const isMalwareResponse = (response: any): response is { query_status: string; data: MalwareIntelligence[] } => {
        return response && typeof response === 'object' && 'query_status' in response;
      };

      // Extract results with proper type checking
      const sandboxResults = sandboxResponse?.data || [];
      const malwareResults = isMalwareResponse(malwareResponse) && malwareResponse.query_status === 'ok' 
        ? malwareResponse.data 
        : [];

      // Create initial results with the group info from the API response
      const initialResults: SearchResult[] = groupResults.map(result => ({
        ...result,
        ttps: [],
        software: [],
        yaraRules: [],
        isLoading: true,
        enrichedData: {
          name: result.common_name,
          mitreId: result.group_id,
          mitreUrl: result.link,
          country: result.coutry, // Use correct backend field
          firstseen: result.year,   // Use correct backend field
          summary: '',
          targets: [],
          aliases: [],
          incidents: [],
          tactics: [],
          enrichment: [],
        },
      }));

      setState({
        results: initialResults,
        sandboxResults,
        malwareResults,
        isLoading: false,
        error: null,
      });

      // Enrich each result with additional data
      for (let i = 0; i < groupResults.length; i++) {
        await fetchGroupData(groupResults[i], i);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'Authentication failed') {
        logout();
        return;
      }
      setState({
        results: [],
        sandboxResults: [],
        malwareResults: [],
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred during search',
      });
    }
  }, [logout]);

  return {
    search,
    results: state.results,
    sandboxResults: state.sandboxResults,
    malwareResults: state.malwareResults,
    isLoading: state.isLoading,
    error: state.error,
  };
}